import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, FileText, AlertCircle } from 'lucide-react';

export default function TermsOfServicePage() {
  const { t } = useTranslation('legal');

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-white to-purple-50/60">
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Back Link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors mb-8 group"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span className="font-medium">{t('backToHome')}</span>
        </Link>

        {/* Header */}
        <div className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-xl p-8 mb-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <FileText className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">{t('terms.title')}</h1>
              <p className="text-gray-600 mt-2">{t('terms.lastUpdated')}</p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-900">{t('terms.intro')}</p>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-xl p-8 space-y-8">

          {/* 1. Acceptance of Terms */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('terms.section1.title')}</h2>
            <div className="space-y-4 text-gray-700">
              <p>{t('terms.section1.p1')}</p>
              <p>{t('terms.section1.p2')}</p>
            </div>
          </section>

          {/* 2. User Accounts */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('terms.section2.title')}</h2>
            <div className="space-y-4 text-gray-700">
              <p>{t('terms.section2.p1')}</p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">{t('terms.section2.subsection1.title')}</h3>
                <ul className="space-y-2 list-disc list-inside">
                  <li>{t('terms.section2.subsection1.item1')}</li>
                  <li>{t('terms.section2.subsection1.item2')}</li>
                  <li>{t('terms.section2.subsection1.item3')}</li>
                  <li>{t('terms.section2.subsection1.item4')}</li>
                </ul>
              </div>
              <p>{t('terms.section2.p2')}</p>
            </div>
          </section>

          {/* 3. User Roles */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('terms.section3.title')}</h2>
            <div className="space-y-4 text-gray-700">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">{t('terms.section3.candidates.title')}</h3>
                <p>{t('terms.section3.candidates.description')}</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">{t('terms.section3.recruiters.title')}</h3>
                <p>{t('terms.section3.recruiters.description')}</p>
              </div>
            </div>
          </section>

          {/* 4. Subscription & Payment */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('terms.section4.title')}</h2>
            <div className="space-y-4 text-gray-700">
              <p>{t('terms.section4.p1')}</p>
              <ul className="space-y-2 list-disc list-inside ml-4">
                <li>{t('terms.section4.item1')}</li>
                <li>{t('terms.section4.item2')}</li>
                <li>{t('terms.section4.item3')}</li>
                <li>{t('terms.section4.item4')}</li>
              </ul>
            </div>
          </section>

          {/* 5. User Content & Intellectual Property */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('terms.section5.title')}</h2>
            <div className="space-y-4 text-gray-700">
              <p>{t('terms.section5.p1')}</p>
              <p>{t('terms.section5.p2')}</p>
              <p>{t('terms.section5.p3')}</p>
            </div>
          </section>

          {/* 6. Prohibited Activities */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('terms.section6.title')}</h2>
            <div className="space-y-4 text-gray-700">
              <p>{t('terms.section6.p1')}</p>
              <ul className="space-y-2 list-disc list-inside ml-4">
                <li>{t('terms.section6.item1')}</li>
                <li>{t('terms.section6.item2')}</li>
                <li>{t('terms.section6.item3')}</li>
                <li>{t('terms.section6.item4')}</li>
                <li>{t('terms.section6.item5')}</li>
                <li>{t('terms.section6.item6')}</li>
                <li>{t('terms.section6.item7')}</li>
              </ul>
            </div>
          </section>

          {/* 7. Termination */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('terms.section7.title')}</h2>
            <div className="space-y-4 text-gray-700">
              <p>{t('terms.section7.p1')}</p>
              <p>{t('terms.section7.p2')}</p>
            </div>
          </section>

          {/* 8. Disclaimers & Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('terms.section8.title')}</h2>
            <div className="space-y-4 text-gray-700">
              <p>{t('terms.section8.p1')}</p>
              <p>{t('terms.section8.p2')}</p>
            </div>
          </section>

          {/* 9. Governing Law */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('terms.section9.title')}</h2>
            <div className="space-y-4 text-gray-700">
              <p>{t('terms.section9.p1')}</p>
            </div>
          </section>

          {/* 10. Changes to Terms */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('terms.section10.title')}</h2>
            <div className="space-y-4 text-gray-700">
              <p>{t('terms.section10.p1')}</p>
            </div>
          </section>

          {/* 11. Contact Information */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('terms.section11.title')}</h2>
            <div className="space-y-4 text-gray-700">
              <p>{t('terms.section11.p1')}</p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="font-medium text-gray-900">{t('terms.section11.companyName')}</p>
                <p>{t('terms.section11.address')}</p>
                <p>{t('terms.section11.email')}</p>
              </div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-6 bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-lg p-6 text-center">
          <p className="text-gray-600">
            {t('terms.footer')}{' '}
            <Link to="/privacy-policy" className="text-blue-600 hover:text-blue-700 font-semibold transition-colors">
              {t('privacyPolicyLink')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
